import { CategoryOptionsListProps } from "../proctoringOptionsGrid";
import OptionBlock from "../optionBlock";
import i18nPackage from "../../../i18n/i18n";
import { useContext, useId, useState } from "react";
import CheckBoxMenuSubItem from "../option-menus/checkBoxMenuSubItem";
import { CoreRangedRadio, RadioOption } from "../../../core";
import styles from "./index.module.css";
import { ConditionalWrapper } from "../../../wrapper";
import { useScrollIntoView } from "../../../hooks";
import { useProctoringOptionContext } from "../../context/EditQuizContext";
import { useProctoringHelpers } from "../hooks/useProctoringHelpers";
import { VerityApiContext } from "../../../api/fetch";
import DisableExtensionList from "../option-lists/DisableExtensionList";
import BlockApplicationList from "../option-lists/BlockApplicationList";

function RestrictHandoffOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();
	const { isOptionAllowed, getPartiallySupportedPlatforms } = useProctoringHelpers();

	const { restrictHandoffMode } = proctoringOption;
	const [bypassWasChecked, setBypassWasChecked] = useState<boolean>(restrictHandoffMode !== "strict");

	return (
		<OptionBlock
			checked={restrictHandoffMode !== "off"}
			updateState={(checked) =>
				setProctoringOption({
					...proctoringOption,
					restrictHandoffMode: checked ? (bypassWasChecked ? "normal" : "strict") : "off",
				})
			}
			optionKey="restrictHandoffMode"
			menuItems={
				isOptionAllowed("restrictHandoffMode", "normal") && isOptionAllowed("restrictHandoffMode", "strict") ? (
					<CheckBoxMenuSubItem
						label={i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.bypassModeText")}
						checked={bypassWasChecked}
						onChange={(checked) => {
							setProctoringOption({
								...proctoringOption,
								restrictHandoffMode: checked ? "normal" : "strict",
							});
							setBypassWasChecked(checked);
						}}
						partiallySupportedPlatforms={getPartiallySupportedPlatforms("restrictHandoffMode", "strict")}
					/>
				) : undefined
			}
		/>
	);
}

function DisableClipboardOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();
	const { isOptionAllowed, getPartiallySupportedPlatforms } = useProctoringHelpers();

	const { disableClipboardMode } = proctoringOption;
	const [systemWasChecked, setSystemWasChecked] = useState<boolean>(disableClipboardMode === "system");

	return (
		<OptionBlock
			optionKey="disableClipboardMode"
			checked={disableClipboardMode !== "off"}
			updateState={(checked) =>
				setProctoringOption({
					...proctoringOption,
					disableClipboardMode: checked ? "tab" : "off",
				})
			}
			menuItems={
				isOptionAllowed("disableClipboardMode", "system") && (
					<CheckBoxMenuSubItem
						label={i18nPackage.t("editQuizPage.configurationStep.disableFeaturesTSX.disableSystemClipboard.header")}
						description={i18nPackage.t(
							"editQuizPage.configurationStep.disableFeaturesTSX.disableSystemClipboard.description",
						)}
						checked={systemWasChecked}
						onChange={(checked) => {
							setProctoringOption({
								...proctoringOption,
								disableClipboardMode: checked ? "system" : "tab",
							});
							setSystemWasChecked(checked);
						}}
						partiallySupportedPlatforms={getPartiallySupportedPlatforms("disableClipboardMode", "system")}
					/>
				)
			}
		/>
	);
}

function BlockExtensionsOption({ extensions }: { extensions: DisableFeaturesOptions["data"]["extensions"] }) {
	const { proctoringOption, setProctoringOption, isMobileLayout } = useProctoringOptionContext();
	const { isOptionAllowed, getPartiallySupportedPlatforms } = useProctoringHelpers();
	const { fetchChromeExtensionData } = useContext(VerityApiContext);

	const { extensionBlockMode, allowedExtensionsList } = proctoringOption;
	const [bypassWasChecked, setBypassWasChecked] = useState<boolean>(extensionBlockMode !== "strict");
	const [focusList, setFocusList] = useState<boolean>(false);

	const checked = extensionBlockMode !== "off";

	return (
		<OptionBlock
			checked={checked}
			updateState={(checked) => {
				setProctoringOption({
					...proctoringOption,
					extensionBlockMode: checked ? (bypassWasChecked ? "normal" : "strict") : "off",
					allowedExtensionsList: [],
				});
				setFocusList(checked);
			}}
			optionKey="extensionBlockMode"
			menuItems={
				isOptionAllowed("extensionBlockMode", "normal") && isOptionAllowed("extensionBlockMode", "strict") ? (
					<CheckBoxMenuSubItem
						label={i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.bypassModeText")}
						checked={bypassWasChecked}
						onChange={(checked) => {
							setProctoringOption({
								...proctoringOption,
								extensionBlockMode: checked ? "normal" : "strict",
							});
							setBypassWasChecked(checked);
						}}
						partiallySupportedPlatforms={getPartiallySupportedPlatforms("extensionBlockMode", "strict")}
					/>
				) : undefined
			}
		>
			{checked && (
				<DisableExtensionList
					allExtensions={extensions}
					initialSelectedExtensionNames={allowedExtensionsList ?? []}
					fetchChromeExtensionData={fetchChromeExtensionData}
					onUpdate={(extensionNames) =>
						setProctoringOption({
							...proctoringOption,
							allowedExtensionsList: extensionNames,
						})
					}
					focusOnMount={focusList && !isMobileLayout}
					onFocus={() => setFocusList(false)}
				/>
			)}
		</OptionBlock>
	);
}

function DisableVirtualCameraOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();
	const { disableVirtualCamera } = proctoringOption;

	return (
		<OptionBlock
			optionKey="disableVirtualCamera"
			checked={Boolean(disableVirtualCamera)}
			updateState={(checked) =>
				setProctoringOption({
					...proctoringOption,
					disableVirtualCamera: checked,
				})
			}
		/>
	);
}

const BLOCK_APPLICATION_MODES = ["off", "block-list", "allow-list", "all"] as const;
type BlockApplicationsMode = (typeof BLOCK_APPLICATION_MODES)[number];

function BlockApplicationsOption({ applications }: { applications: DisableFeaturesOptions["data"]["applications"] }) {
	const { proctoringOption, setProctoringOption, isMobileLayout } = useProctoringOptionContext();
	const { isOptionAllowed } = useProctoringHelpers();

	const {
		applicationBlockMode,
		blockWindowedApplications,
		hideOtherWindows,
		applicationBlockList,
		allowedApplicationsList,
		oneScreenEnforcementMode,
	} = proctoringOption;
	const [bypassWasChecked, setBypassWasChecked] = useState<boolean>(applicationBlockMode !== "strict");
	const [focusList, setFocusList] = useState<boolean>(false); // Focus list if user opens the list

	const { ref } = useScrollIntoView<HTMLDivElement>();

	const notAllowed = !isOptionAllowed("applicationBlockMode");
	const disableBlockWindowed = notAllowed || !isOptionAllowed("blockWindowedApplications");
	const disableBlockAll = disableBlockWindowed || !isOptionAllowed("hideOtherWindows");

	let currMode: BlockApplicationsMode;
	if (applicationBlockMode === "off") {
		currMode = "off";
	} else if (hideOtherWindows) {
		currMode = "all";
	} else if (blockWindowedApplications) {
		currMode = "allow-list";
	} else {
		currMode = "block-list";
	}

	const getHideOtherWindowsOptionUpdates = (hide: boolean) => ({
		hideOtherWindows: hide,
		oneScreenEnforcementMode: hide && oneScreenEnforcementMode === "off" ? "normal" : oneScreenEnforcementMode,
	});

	function getRadioIsDisabled(mode: BlockApplicationsMode) {
		let disabled = notAllowed;
		if (!disabled) {
			if (mode === "allow-list") {
				disabled = disableBlockWindowed;
			} else if (mode === "all") {
				disabled = disableBlockAll;
			}
		}
		return disabled;
	}

	const headerId = useId();

	const radioOptions: RadioOption[] = BLOCK_APPLICATION_MODES.map((mode) => ({
		value: mode,
		label: i18nPackage.t(`editQuizPage.configurationStep.disableFeaturesTSX.blockApplications.modes.${mode}`),
		disabled: getRadioIsDisabled(mode),
	}));

	function handleBlockAppOnChange(_: React.ChangeEvent<HTMLInputElement>, value: string) {
		const defaultBlockMode = bypassWasChecked ? "normal" : "strict";

		switch (value) {
			case "off": {
				setProctoringOption({
					...proctoringOption,
					applicationBlockMode: "off",
					blockWindowedApplications: false,
					...getHideOtherWindowsOptionUpdates(false),
				});
				break;
			}

			case "block-list":
				setProctoringOption({
					...proctoringOption,
					applicationBlockMode: applicationBlockMode === "off" ? defaultBlockMode : applicationBlockMode,
					blockWindowedApplications: false,

					...getHideOtherWindowsOptionUpdates(false),
				});
				if (!isMobileLayout) setFocusList(true);
				break;

			case "allow-list":
				setProctoringOption({
					...proctoringOption,
					applicationBlockMode: applicationBlockMode === "off" ? defaultBlockMode : applicationBlockMode,
					blockWindowedApplications: true,

					...getHideOtherWindowsOptionUpdates(false),
				});
				if (!isMobileLayout) setFocusList(true);
				break;

			case "all": {
				setProctoringOption({
					...proctoringOption,
					applicationBlockMode: applicationBlockMode === "off" ? defaultBlockMode : applicationBlockMode,
					blockWindowedApplications: true,

					...getHideOtherWindowsOptionUpdates(true),
				});
				break;
			}
		}
	}

	return (
		<OptionBlock
			optionKey="applicationBlockMode"
			checked={currMode !== "off"}
			menuItems={
				applicationBlockMode !== "off" &&
				isOptionAllowed("applicationBlockMode", "normal") &&
				isOptionAllowed("applicationBlockMode", "strict") && (
					<CheckBoxMenuSubItem
						label={i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.bypassModeText")}
						checked={bypassWasChecked}
						onChange={(checked) => {
							setProctoringOption({
								...proctoringOption,
								applicationBlockMode: checked ? "normal" : "strict",
							});
							setBypassWasChecked(checked);
						}}
					/>
				)
			}
		>
			<ConditionalWrapper
				condition={isMobileLayout}
				whenTrue={(children) => <div className={styles.blockApplicationsModesContainer}>{children}</div>}
			>
				<div style={{ width: isMobileLayout ? "fit-content" : undefined }} ref={ref}>
					<CoreRangedRadio
						curValue={currMode}
						onChange={handleBlockAppOnChange}
						radioGroupAriaLabelledby={headerId}
						radioOptions={radioOptions}
						startLabel={i18nPackage.t(
							"editQuizPage.configurationStep.disableFeaturesTSX.blockApplications.leastRestrictive",
						)}
						endLabel={i18nPackage.t(
							"editQuizPage.configurationStep.disableFeaturesTSX.blockApplications.mostRestrictive",
						)}
					/>
				</div>
			</ConditionalWrapper>

			{(currMode === "allow-list" || currMode === "block-list") && (
				<BlockApplicationList
					key={currMode}
					focusOnMount={focusList}
					onFocus={() => setFocusList(false)}
					action={currMode === "block-list" ? "block" : "allow"}
					allApplications={applications}
					initialSelectedProcessNames={
						currMode === "block-list" ? (applicationBlockList ?? []) : (allowedApplicationsList ?? [])
					}
					onUpdate={(processNames) => {
						if (currMode === "block-list") {
							setProctoringOption({
								...proctoringOption,
								applicationBlockList: processNames,
								applicationBlockMode: applicationBlockMode,
							});
						} else {
							setProctoringOption({
								...proctoringOption,
								allowedApplicationsList: processNames,
								...getHideOtherWindowsOptionUpdates(processNames.length === 0),
							});
						}
					}}
				/>
			)}
		</OptionBlock>
	);
}

function MonitorDesktopApp() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();
	const { recordApplicationsActivity } = proctoringOption;

	return (
		<OptionBlock
			optionKey="recordApplicationsActivity"
			checked={Boolean(recordApplicationsActivity)}
			updateState={(checked) =>
				setProctoringOption({
					...proctoringOption,
					recordApplicationsActivity: checked,
				})
			}
		/>
	);
}

function ViewRunningApplicationsOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();
	const { logRunningApplications } = proctoringOption;

	return (
		<OptionBlock
			optionKey="logRunningApplications"
			checked={Boolean(logRunningApplications)}
			updateState={(checked) =>
				setProctoringOption({
					...proctoringOption,
					logRunningApplications: checked,
				})
			}
		/>
	);
}

function GuidedAccessOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();
	const { enableGuidedAccessMode } = proctoringOption;

	return (
		<OptionBlock
			optionKey="enableGuidedAccessMode"
			checked={Boolean(enableGuidedAccessMode)}
			updateState={(checked) =>
				setProctoringOption({
					...proctoringOption,
					enableGuidedAccessMode: checked,
				})
			}
		/>
	);
}

function DisableDownloadFileOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();
	const { disableDownloadFiles } = proctoringOption;

	return (
		<OptionBlock
			optionKey="disableDownloadFiles"
			checked={Boolean(disableDownloadFiles)}
			updateState={(checked) =>
				setProctoringOption({
					...proctoringOption,
					disableDownloadFiles: checked,
				})
			}
		/>
	);
}

function DisableSpellcheckOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();
	const { disableSpellcheck } = proctoringOption;

	return (
		<OptionBlock
			optionKey="disableSpellcheck"
			checked={Boolean(disableSpellcheck)}
			updateState={(checked) =>
				setProctoringOption({
					...proctoringOption,
					disableSpellcheck: checked,
				})
			}
		/>
	);
}

// TO DO ENABLE BUT DISABLED MESSAGE?
function DisablePrintingOption() {
	const { proctoringOption, setProctoringOption, enforcedProfilesOnly } = useProctoringOptionContext();
	const { isOptionAllowed } = useProctoringHelpers();

	const { disablePrinting } = proctoringOption;
	const disablePrintingAllowed = isOptionAllowed("disablePrinting");

	// Enable but disable
	const forceEnable = proctoringOption.isLockdown;

	return (
		<OptionBlock
			optionKey="disablePrinting"
			checked={forceEnable ? true : Boolean(disablePrinting)}
			updateState={(checked) =>
				setProctoringOption({
					...proctoringOption,
					disablePrinting: checked,
				})
			}
			disabled={!disablePrintingAllowed || enforcedProfilesOnly || forceEnable}
		/>
	);
}

function DisableRightClickOption({ hasPassword }: { hasPassword: boolean }) {
	const { proctoringOption, setProctoringOption, enforcedProfilesOnly } = useProctoringOptionContext();
	const { isOptionAllowed } = useProctoringHelpers();

	const { disableRightClick, isLockdown } = proctoringOption;
	const disableRightClickAllowed = isOptionAllowed("disableRightClick");

	return (
		<OptionBlock
			optionKey="disableRightClick"
			checked={isLockdown ? true : Boolean(disableRightClick)}
			updateState={(checked) =>
				setProctoringOption({
					...proctoringOption,
					disableRightClick: checked,
				})
			}
			disabled={
				!disableRightClickAllowed ||
				(hasPassword && !proctoringOption.disableRightClick) ||
				enforcedProfilesOnly ||
				isLockdown
			}
			disabledMessage={
				isLockdown
					? null
					: hasPassword
						? i18nPackage.t("editQuizPage.configurationStep.disableFeaturesTSX.rightClickPasswordConflictTxt")
						: undefined
			}
		/>
	);
}

function BlockVirtualMachineOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();
	const { blockVirtualMachines } = proctoringOption;

	return (
		<OptionBlock
			optionKey="blockVirtualMachines"
			checked={Boolean(blockVirtualMachines)}
			updateState={(checked) =>
				setProctoringOption({
					...proctoringOption,
					blockVirtualMachines: checked,
				})
			}
		/>
	);
}

type DisableFeaturesOptions = CategoryOptionsListProps;

// DisableFeatures and OtherActivities got combined, but keeping it separate so file doesn't become huge
export default function DisableFeaturesOptions({ hasPassword = false, data }: DisableFeaturesOptions) {
	const { shouldShowOption } = useProctoringHelpers();

	return (
		<>
			{shouldShowOption("disableVirtualCamera") && <DisableVirtualCameraOption />}
			{shouldShowOption("disableClipboardMode") && <DisableClipboardOption />}

			{shouldShowOption("enableGuidedAccessMode") && <GuidedAccessOption />}

			{shouldShowOption("disableDownloadFiles") && <DisableDownloadFileOption />}

			{shouldShowOption("disableSpellcheck") && <DisableSpellcheckOption />}

			{shouldShowOption("disablePrinting") && <DisablePrintingOption />}

			{shouldShowOption("disableRightClick") && <DisableRightClickOption hasPassword={hasPassword} />}

			{shouldShowOption("extensionBlockMode") && <BlockExtensionsOption extensions={data.extensions} />}

			{shouldShowOption("restrictHandoffMode") && <RestrictHandoffOption />}

			{shouldShowOption("blockVirtualMachines") && <BlockVirtualMachineOption />}
			{shouldShowOption("recordApplicationsActivity") && <MonitorDesktopApp />}
			{shouldShowOption("logRunningApplications") && <ViewRunningApplicationsOption />}
			{shouldShowOption("applicationBlockMode") && <BlockApplicationsOption applications={data.applications} />}
		</>
	);
}
